import makeApiCall from "./master/axiosIndex";

// Activity-level daily execution: the site keys one number per face per day and
// every dependent BOQ line's executed quantity is derived from the activity's
// execution norms. All decimals cross the wire as STRINGS; an empty string
// means "not measurable" and is never a fake '0'.

export type ExecutionSlotKey = "SLOT_1" | "SLOT_2" | "SLOT_3" | "SLOT_4" | "SLOT_5";

export interface ExecutionSlotDef {
  slotKey: ExecutionSlotKey;
  slotLabel: string;
  uomId?: number;
  // Resolved server-side so no screen shows a raw uom id. Read-only.
  uomName?: string;
  isActive?: boolean;
  displayOrder?: number;
}

export interface ExecutionNormCell {
  taskId: number;
  slotKey: ExecutionSlotKey;
  normValue: string; // full precision — never round the norm
}

export interface ExecutionBoqLine {
  taskId: number;
  internalTaskNumber: string; // '<activityCode>/<boqNo>', e.g. 'AE-0101/B1.1'
  taskSequenceId: string;
  taskName: string;
  workQuantity: string;
  workUomId: number;
  workUomName: string;
  // Task Monitoring: true when the task is tracked on the date the norms were
  // asked for (`asOfDate`). gRPC omits false, so read it as `=== true`.
  isMonitored?: boolean;
}

// Which derived lines the site's measured actuals are expected against.
// 'MONITORED' = only the tracked lines (the drawer's "Tracked lines only"
// filter); 'ALL' = every derived line.
export type MeasuredScope = "ALL" | "MONITORED";

export interface ExecutionSlotQty {
  slotKey: ExecutionSlotKey;
  slotLabel?: string;
  uomId?: number;
  uomName?: string;
  actualExecutionQty: string;
}

// One link in an activity's norm-matrix chain. The matrix is effective-dated,
// so "the norms" is always "the norms as of a date".
export interface ExecutionNormVersion {
  versionId: number;
  effectiveFrom: string;   // 'YYYY-MM-DD'
  // Last day this version applies — '' while it is still in force. Derived from
  // the chain server-side, never stored.
  effectiveTo: string;
  remarks: string;
  status: "Active" | "Superseded";
  isCurrent: boolean;
  createdAt?: string;
  createdBy?: number;
}

// 'correct'    — rewrite the CURRENT version in place, keeping its effective
//                date, so the fix reaches days already captured under it.
// 'newVersion' — append a version from a date; earlier days keep the norms they
//                were derived with.
export type ExecutionNormSaveMode = "correct" | "newVersion";

export interface ExecutionRecalcChange {
  entryId: number;
  executionDate: string;
  fromVersionId: number;
  toVersionId: number;
  effectiveFrom: string;
  taskId: number;
  taskLabel: string;
  previousValue: string;
  newValue: string;
  delta: string;
}

export const executionNormApis = {
  // Full replace-set of the activity's slot definitions AND norm matrix.
  // An omitted cell means "not applicable" and will be deleted — that is the
  // contract, matching the config screen's load-edit-save cycle.
  //
  // `mode` decides whether the change reaches days already captured. Omitted =
  // 'correct', which is the pre-versioning behaviour.
  save: (
    activityId: number,
    slots: ExecutionSlotDef[],
    norms: ExecutionNormCell[],
    version?: {
      mode?: ExecutionNormSaveMode;
      effectiveFrom?: string;
      remarks?: string;
    }
  ): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/norms/save`,
      method: "post",
      data: { activityId, slots, norms, ...(version || {}) },
    }),

  // `versionId` browses an older matrix; omitted loads the newest, which is
  // what the config screen opens on. `asOfDate` ('YYYY-MM-DD') resolves each
  // task's `isMonitored` flag on that date and fills `monitoringConfigured` /
  // `monitoredTaskCount` on the response.
  getByActivity: (
    activityId: number | string,
    versionId?: number,
    asOfDate?: string
  ): Promise<any> => {
    const params: Record<string, any> = {};
    if (versionId) params.versionId = versionId;
    if (asOfDate) params.asOfDate = asOfDate;
    return makeApiCall({
      url: `/activity-execution/norms/activity/${activityId}`,
      method: "get",
      ...(Object.keys(params).length ? { params } : {}),
    });
  },

  listVersions: (activityId: number | string): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/norms/activity/${activityId}/versions`,
      method: "get",
    }),

  // Restates already-captured days against the version in force on each of
  // their dates. `preview` defaults TRUE server-side — a body that forgets the
  // flag computes rather than commits.
  recalculate: (payload: {
    activityId: number;
    fromDate: string;
    toDate: string;
    preview?: boolean;
    staleOnly?: boolean;
  }): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/norms/recalculate`,
      method: "post",
      data: payload,
    }),

  // `asOfDate` fills `activities[].trackedTaskCount` for that date.
  listActivities: (
    projectId: number | string,
    filters: { phaseId?: number | string; search?: string; asOfDate?: string } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "search", "asOfDate"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/activity-execution/norms/project/${projectId}/activities`,
      method: "get",
      params,
    });
  },

  uploadExcel: (formData: FormData): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/norms/upload/excel`,
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export const activityExecutionApis = {
  // Upsert keyed on (activityId, executionDate, shiftId): saving the same
  // activity and date EDITS that entry rather than creating a second one.
  save: (payload: {
    activityId: number;
    executionDate: string; // 'YYYY-MM-DD'
    shiftId?: number;
    // The window actually worked, 'HH:MM'. Duration is derived server-side.
    startTime?: string;
    endTime?: string;
    slots: ExecutionSlotQty[];
    remarks?: string;
    // Site-measured actuals per derived line (the measurement tier).
    measured?: Array<{
      taskId: number;
      measuredValue: string;
      remarks?: string;
      stretchActuals?: Array<{ fromChainage: string; toChainage: string; measuredValue: string }>;
    }>;
    // Omitted = 'ALL'. Response carries `monitoredLineCount`,
    // `monitoringConfigured` and `lines[].isMonitored`.
    measuredScope?: MeasuredScope;
  }): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/save`,
      method: "post",
      data: payload,
    }),

  // By entryId, or by activity + date. The second form is what lets the form
  // reveal an existing entry to edit instead of silently duplicating it —
  // `exists: false` is a normal answer, not an error.
  getEntry: (params: {
    entryId?: number;
    activityId?: number;
    executionDate?: string;
    shiftId?: number;
  }): Promise<any> => {
    const query: Record<string, any> = {};
    (["entryId", "activityId", "executionDate", "shiftId"] as const).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && `${value}`.trim?.() !== "") {
        query[key] = value;
      }
    });
    return makeApiCall({
      url: `/activity-execution/entry`,
      method: "get",
      params: query,
    });
  },

  list: (
    projectId: number | string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      fromDate?: string;
      toDate?: string;
      pageNo?: number;
      pageSize?: number;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "fromDate", "toDate", "pageNo", "pageSize"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/activity-execution/project/${projectId}`,
      method: "get",
      params,
    });
  },

  // Civil Excel upload — "Execution" + "Actuals" sheets; the reply id is the
  // upload report the stage modal polls.
  uploadDailyExcel: (formData: FormData): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/daily/upload/excel`,
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    }),

  getRollup: (activityId: number | string): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/rollup/activity/${activityId}`,
      method: "get",
    }),

  deleteEntry: (entryId: number | string): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/entry/${entryId}`,
      method: "delete",
    }),
};

// Drive & Chainage Ledger (D1). The chain is computed server-side from the
// captured stretches — these read it; setChainage writes the ACTIVITY's design
// chainage (no drive master).
export const driveChainApis = {
  list: (projectId: number | string, phaseId?: number): Promise<any> => {
    const params: Record<string, any> = {};
    if (phaseId) params.phaseId = phaseId;
    return makeApiCall({
      url: `/activity-execution/drives/project/${projectId}`,
      method: "get",
      params,
    });
  },

  get: (activityId: number | string): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/drives/activity/${activityId}`,
      method: "get",
    }),

  setChainage: (payload: {
    activityId: number;
    startChainage: string;
    endChainage: string;
  }): Promise<any> =>
    makeApiCall({
      url: `/activity-execution/drives/chainage`,
      method: "post",
      data: payload,
    }),
};

// Evidence photos for a line's ACTUAL work done — up to 3, riding
// gateWay/dms/upload-multi exactly like the mobile app's uploads. Linked to
// the entry with the line named in docName/remarks; never blocks the save.
export const uploadActualPhotosWeb = (params: {
  entryId: number;
  taskLabel: string;
  executionDate: string;
  files: File[];
}): Promise<any> => {
  const form = new FormData();
  params.files.forEach((file) => form.append("files", file, file.name));
  form.append(
    "filesData",
    JSON.stringify({
      newfiledata: params.files.map((file, i) => ({
        indexOfFileLocated: i,
        linkType: "execution-actual",
        action: "actual work photo",
        docName: `${params.taskLabel} — ${file.name}`,
        linkTo: String(params.entryId),
        remarks: `${params.taskLabel} · ${params.executionDate}`,
      })),
      deletedFileData: [],
    })
  );
  return makeApiCall({
    url: `/dms/upload-multi`,
    method: "post",
    data: form,
    headers: { "Content-Type": "multipart/form-data" },
  });
};
